<template>
  <div class="flex flex-col h-full">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <Spinner :color="'text-black'" :size="10" />
    </div>

    <template v-else>
      <!-- Engineer Info -->
      <div class="flex items-center mb-4 pb-4 border-b">
        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-lg mr-3">
          {{ engineerInitials }}
        </div>
        <div>
          <div class="font-semibold text-lg">{{ engineerName }}</div>
          <div v-if="engineerEmail" class="text-xs text-gray-400">{{ engineerEmail }}</div>
        </div>
      </div>

      <!-- Stats -->
      <div class="flex gap-3 mb-4">
        <div class="stat-card stat-booked">
          <div class="stat-icon">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
          </div>
          <div class="stat-value">{{ stats.scheduleDays }}</div>
          <div class="stat-label">Booked</div>
        </div>
        <div class="stat-card stat-leave">
          <div class="stat-icon">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
          </div>
          <div class="stat-value">{{ stats.leaveDays }}</div>
          <div class="stat-label">Leave</div>
        </div>
        <div class="stat-card stat-available">
          <div class="stat-icon">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
          </div>
          <div class="stat-value">{{ stats.availableDays }}</div>
          <div class="stat-label">Available</div>
        </div>
      </div>

      <!-- Calendar -->
      <div class="flex-1 overflow-y-auto">
        <FullCalendar
            ref="fullCalendar"
            class="availability-calendar"
            :options="calendarOptions"
        >
          <template v-slot:eventContent="arg">
            <div class="event-content">
              <i>{{ arg.event.title }}</i>
            </div>
          </template>
        </FullCalendar>

        <!-- Events loading indicator -->
        <div v-if="isLoadingEvents" class="events-loading">
          <div class="events-loading-bar"></div>
          <span class="events-loading-text">Loading availability...</span>
        </div>
      </div>

      <!-- Legend -->
      <div class="flex items-center gap-4 mt-3 pt-3 border-t text-xs text-gray-400">
        <div class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background-color: #e0e7ff;"></span> Schedule Period</div>
        <div class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background-color: #3b82f6;"></span> This Schedule</div>
        <div class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background-color: #9ca3af;"></span> Other Schedule</div>
        <div class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background-color: #ef4444;"></span> Leave</div>
      </div>
    </template>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";
import ScheduleService from "@/services/scheduleService";
import HolidayService from "@/services/holidayService";
import Spinner from "@/components/Spinner.vue";

export default {
  name: "EngineerAvailabilityPanel",
  components: { FullCalendar, Spinner },
  props: {
    engineerId: { type: String, required: true },
    engineerName: { type: String, default: "" },
    engineerEmail: { type: String, default: "" },
    scheduleId: { type: String, default: null },
    scheduleStartDate: { type: [Date, Number], default: null },
    scheduleEndDate: { type: [Date, Number], default: null },
  },
  data() {
    return {
      isLoading: false,
      isLoadingEvents: false,
      initialLoadDone: false,
      currentViewStart: null,
      currentViewEnd: null,
      stats: {
        scheduleDays: 0,
        leaveDays: 0,
        availableDays: 0,
      },
    };
  },
  computed: {
    engineerInitials() {
      if (!this.engineerName) return "?";
      const parts = this.engineerName.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return parts[0][0].toUpperCase();
    },
    initialDate() {
      if (this.scheduleStartDate) {
        if (typeof this.scheduleStartDate === "number") {
          return new Date(this.scheduleStartDate * 1000);
        }
        return this.scheduleStartDate;
      }
      return new Date();
    },
    calendarOptions() {
      return {
        plugins: [dayGridPlugin, listPlugin],
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth listWeek",
        },
        initialView: "dayGridMonth",
        initialDate: this.initialDate,
        buttonText: {
          today: "This Month",
          month: "Month",
          list: "List",
        },
        events: [],
        height: "auto",
        contentHeight: "auto",
        dayMaxEvents: 3,
        weekends: true,
        fixedWeekCount: false,
        eventClassNames: function (arg) {
          if (arg.event.display === "background") return [""];
          return ["fc-events"];
        },
        eventDidMount: function (info) {
          if (info.event.display === "background") return;
          const props = info.event.extendedProps || {};
          let content = "";
          if (props.type === "assignment") {
            const parts = [];
            if (props.job_id) parts.push("<strong>Job:</strong> " + props.job_id);
            if (props.customer) parts.push("<strong>Customer:</strong> " + props.customer);
            if (props.status) parts.push("<strong>Status:</strong> " + props.status);
            content = parts.join("<br>");
          } else if (props.type === "leave") {
            const parts = [];
            if (props.leave_type) parts.push("<strong>Type:</strong> " + props.leave_type);
            if (props.status) parts.push("<strong>Status:</strong> " + props.status);
            if (props.purpose) parts.push("<strong>Reason:</strong> " + props.purpose);
            content = parts.join("<br>");
          }
          if (content) {
            tippy(info.el, {
              content: content,
              allowHTML: true,
              theme: "material",
            });
          }
        },
        eventWillUnmount: function (info) {
          if (info.el._tippy) {
            info.el._tippy.destroy();
          }
        },
        datesSet: this.onDatesSet,
      };
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    onDatesSet(info) {
      this.currentViewStart = info.start;
      this.currentViewEnd = info.end;
      if (!this.initialLoadDone) return;
      const fromDate = this.$moment(info.start).format("DD/MM/YYYY");
      const toDate = this.$moment(info.end).format("DD/MM/YYYY");
      this.loadCalendarData(fromDate, toDate);
    },

    async loadData() {
      this.isLoading = true;
      try {
        const fromDate = this.$moment(this.initialDate).subtract(1, "month").startOf("month").format("DD/MM/YYYY");
        const toDate = this.$moment(this.initialDate).add(2, "months").endOf("month").format("DD/MM/YYYY");
        await this.loadCalendarData(fromDate, toDate);
      } catch (err) {
        console.error("Error loading availability:", err);
      } finally {
        this.isLoading = false;
        this.isLoadingEvents = false;
        this.initialLoadDone = true;
      }
    },

    async loadCalendarData(fromDate, toDate) {
      this.isLoadingEvents = true;
      try {
      const events = [];
      let scheduleDays = 0;
      let leaveDays = 0;

      // 1. Current schedule date range — background highlight
      if (this.scheduleStartDate && this.scheduleEndDate) {
        const startStr = typeof this.scheduleStartDate === "number"
            ? this.$moment.unix(this.scheduleStartDate).format("YYYY-MM-DD")
            : this.$moment(this.scheduleStartDate).format("YYYY-MM-DD");
        const endStr = typeof this.scheduleEndDate === "number"
            ? this.$moment.unix(this.scheduleEndDate).add(1, "day").format("YYYY-MM-DD")
            : this.$moment(this.scheduleEndDate).add(1, "day").format("YYYY-MM-DD");

        events.push({
          start: startStr,
          end: endStr,
          display: "background",
          backgroundColor: "#e0e7ff",
        });
      }

      // 2. Schedule assignments for this engineer
      try {
        const calendarData = await ScheduleService.getCalendar(
            `?engineer_id=${this.engineerId}&fromDate=${fromDate}&toDate=${toDate}`
        );
        const assignments = calendarData.data || calendarData || [];
        assignments.forEach(a => {
          const isThisSchedule = this.scheduleId && a.schedule_id === this.scheduleId;
          const dateStr = new Date(a.scheduled_date * 1000).toISOString().split("T")[0];
          const endStr = new Date((a.scheduled_date + 86400) * 1000).toISOString().split("T")[0];
          const customerName = (a.customer && a.customer.name) || (a.schedule && a.schedule.customer && a.schedule.customer.name) || "";
          const jobId = (a.schedule && a.schedule.job_id) || a.job_id || "";

          events.push({
            id: a.assignment_id || `assign-${a.scheduled_date}`,
            title: isThisSchedule ? "This Schedule" : (jobId || "Other"),
            start: dateStr,
            end: endStr,
            backgroundColor: isThisSchedule ? "#3b82f6" : "#9ca3af",
            borderColor: isThisSchedule ? "#2563eb" : "#6b7280",
            textColor: "#ffffff",
            allDay: true,
            extendedProps: {
              type: "assignment",
              schedule_id: a.schedule_id,
              job_id: jobId,
              customer: customerName,
              status: a.status,
            },
          });

          scheduleDays++;
        });
      } catch (err) {
        console.error("Error fetching calendar assignments:", err);
      }

      // 3. Leave data for this engineer
      try {
        const leaveResponse = await HolidayService.getLeaveSummary(
            {
              searchStaffId: this.engineerId,
              status: "approved",
              months_prior: 1,
              months_post: 3,
            },
            this.engineerId,
        );
        const leaves = leaveResponse.data || leaveResponse || [];
        leaves.forEach(leave => {
          const startDate = this.$moment(leave.start_date, "DD-MM-YYYY").toDate();
          let endDate = this.$moment(leave.end_date, "DD-MM-YYYY").toDate();
          endDate.setDate(endDate.getDate() + 1); // FullCalendar end is exclusive

          const daysDiff = this.$moment(leave.end_date, "DD-MM-YYYY").diff(
              this.$moment(leave.start_date, "DD-MM-YYYY"), "days"
          ) + 1;
          leaveDays += daysDiff;

          events.push({
            id: `leave-${leave.leave_id || leave._id || Math.random()}`,
            title: leave.leave_type || "Leave",
            start: startDate,
            end: endDate,
            backgroundColor: "#ef4444",
            borderColor: "#dc2626",
            textColor: "#ffffff",
            allDay: true,
            extendedProps: {
              type: "leave",
              leave_type: leave.leave_type,
              status: leave.status,
              purpose: leave.purpose || "",
            },
          });
        });
      } catch (err) {
        console.error("Error fetching leave data:", err);
      }

      // Calculate available working days in the currently viewed month
      const viewAnchor = this.currentViewStart || this.initialDate;
      const monthStart = this.$moment(viewAnchor).startOf("month");
      const monthEnd = this.$moment(viewAnchor).endOf("month");
      let workingDays = 0;
      let d = monthStart.clone();
      while (d.isSameOrBefore(monthEnd, "day")) {
        if (d.day() !== 0 && d.day() !== 6) workingDays++;
        d.add(1, "day");
      }

      this.stats = {
        scheduleDays,
        leaveDays,
        availableDays: Math.max(0, workingDays - scheduleDays - leaveDays),
      };

      // Use FullCalendar API to update events (not reactive data)
      // This prevents the calendar from re-rendering and firing datesSet again
      if (this.$refs.fullCalendar) {
        const calendarApi = this.$refs.fullCalendar.getApi();
        const existingSources = calendarApi.getEventSources();
        existingSources.forEach(source => source.remove());
        calendarApi.addEventSource(events);
      }

      this.isLoadingEvents = false;
    } catch (err) {
      console.error("Error in loadCalendarData:", err);
      this.isLoadingEvents = false;
    }
    },
  },
};
</script>

<style scoped>
/* ── Stat cards ── */
.stat-card {
  flex: 1;
  border-radius: 8px;
  padding: 10px 8px;
  text-align: center;
  border: 1px solid transparent;
  position: relative;
}
.stat-icon {
  margin: 0 auto 4px auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.stat-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}
.stat-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-top: 2px;
}

.stat-booked {
  background: #eff6ff;
  border-color: #bfdbfe;
}
.stat-booked .stat-icon { background: #dbeafe; color: #2563eb; }
.stat-booked .stat-value { color: #1d4ed8; }
.stat-booked .stat-label { color: #3b82f6; }

.stat-leave {
  background: #fef2f2;
  border-color: #fecaca;
}
.stat-leave .stat-icon { background: #fee2e2; color: #dc2626; }
.stat-leave .stat-value { color: #dc2626; }
.stat-leave .stat-label { color: #ef4444; }

.stat-available {
  background: #f0fdf4;
  border-color: #bbf7d0;
}
.stat-available .stat-icon { background: #dcfce7; color: #16a34a; }
.stat-available .stat-value { color: #15803d; }
.stat-available .stat-label { color: #22c55e; }

.availability-calendar {
  font-size: 12px;
  position: relative;
}

/* Events loading indicator */
.events-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}
.events-loading-bar {
  height: 3px;
  width: 60px;
  border-radius: 2px;
  background: #e5e7eb;
  overflow: hidden;
  position: relative;
}
.events-loading-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: -60px;
  width: 60px;
  height: 100%;
  background: #f59e0b;
  border-radius: 2px;
  animation: loading-slide 1s ease-in-out infinite;
}
@keyframes loading-slide {
  0% { left: -60px; }
  100% { left: 60px; }
}
.events-loading-text {
  font-size: 11px;
  color: #9ca3af;
}

::v-deep .fc-events {
  border-radius: 4px !important;
  padding: 1px 4px !important;
  font-size: 10px !important;
  cursor: default;
}

::v-deep .fc .fc-toolbar {
  font-size: 12px;
}

::v-deep .fc .fc-toolbar-title {
  font-size: 14px !important;
}

::v-deep .fc .fc-button {
  font-size: 11px !important;
  padding: 4px 8px !important;
}

.event-content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
}

/* Today: border highlight instead of background fill */
::v-deep .fc .fc-day-today {
  background-color: transparent !important;
}
::v-deep .fc .fc-daygrid-day.fc-day-today {
  border: 2px solid #3b82f6 !important;
}
::v-deep .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
  font-weight: 700;
  color: #3b82f6;
}
::v-deep .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number::after {
  content: " Today";
  font-size: 9px;
  font-weight: 600;
  color: #3b82f6;
}
</style>
